import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BadgeCheck, Banknote, BatteryCharging, Calculator, ClipboardCheck,
  FileCheck, Handshake, Phone, PiggyBank, ShieldCheck, Sun, TrendingDown,
} from "lucide-react";
import { FadeUp, Overline, SectionHead } from "@/components/site/Reveal";

const HERO_IMG = "/api/files/melwest-eco-energy/brand/trade-stc-hero.jpeg";
const STC_RATE = 35; // indicative trade rate, $/STC

// Cheaper Home Batteries Program — STC factor steps down over time (CER schedule)
const PERIODS = [
  { value: 8.4, label: "Jan – Apr 2026 — 8.4 STC/kWh" },
  { value: 6.8, label: "May – Dec 2026 — 6.8 STC/kWh (current)" },
  { value: 5.7, label: "Jan – Jun 2027 — 5.7 STC/kWh" },
  { value: 5.2, label: "Jul – Dec 2027 — 5.2 STC/kWh" },
];
const CURRENT_PERIOD = PERIODS[1].value;

// Certificates are calculated on usable capacity in tiers:
// first 14 kWh @ 100%, 14-28 kWh @ 60%, 28-50 kWh @ 15% (first 50 kWh only)
function countedCapacity(kwh) {
  const capped = Math.min(kwh, 50);
  const full = Math.min(capped, 14);
  const mid = Math.min(Math.max(capped - 14, 0), 14);
  const top = Math.max(capped - 28, 0);
  return full + mid * 0.6 + top * 0.15;
}

const STEPS = [
  {
    icon: Handshake, title: "You do the job",
    copy: "Install batteries — or solar with storage — for your own customers. Your rate, your relationship, your invoice.",
  },
  {
    icon: BadgeCheck, title: "We check compliance",
    copy: "Send us the job paperwork. We check it against CEC, Solar Victoria and Clean Energy Regulator rules before anything is lodged.",
  },
  {
    icon: FileCheck, title: "We claim the certificates",
    copy: "We create, register and trade the battery STCs on your behalf — including the REC Registry lodgement and audit-ready evidence.",
  },
  {
    icon: Banknote, title: "You get paid trade rates",
    copy: "You receive trade pricing across our range and the certificate value passed through at market rates, with fast settlement terms.",
  },
];

const TREND_STATS = [
  { value: "457,000+", label: "home batteries approved in the federal rebate's first year" },
  { value: "276,011", label: "batteries installed in the first half of 2026 alone — up 52%" },
  { value: "15.3 GWh", label: "of approved battery capacity and climbing" },
  { value: "$35/STC", label: "current indicative trade rate we pass through" },
];

const BENEFITS = [
  {
    icon: Sun, title: "Solar after dark",
    copy: "Batteries store the middle-of-the-day surplus and run the home through the evening — the bit that used to cost the most.",
  },
  {
    icon: BatteryCharging, title: "Backup when the grid drops",
    copy: "Keep the lights, fridge and internet on through outages with whole-home or essential-circuit backup.",
  },
  {
    icon: TrendingDown, title: "Beat evening peak rates",
    copy: "Run on stored sunshine instead of paying peak tariffs — the fastest-growing reason customers give for buying.",
  },
  {
    icon: PiggyBank, title: "Upfront federal discount",
    copy: "The Cheaper Home Batteries Program pays a point-of-sale discount via STCs — we help you claim it on every eligible job.",
  },
];

const WE_HANDLE = [
  "Compliance review of every job before lodgement",
  "Photo and paperwork evidence packaged audit-ready",
  "Battery STC creation and registration with the REC Registry",
  "Eligibility checks — CEC approved batteries, 5–100 kWh nominal capacity",
  "Certificate trading at the current market rate",
  "Fast payment — certificate value passed through to you",
];

function BatteryStcCalculator() {
  const [kwh, setKwh] = useState(13.5);
  const [factor, setFactor] = useState(CURRENT_PERIOD);

  const counted = countedCapacity(kwh);
  const stcs = Math.floor(counted * factor);
  const value = stcs * STC_RATE;
  const periodLabel = (PERIODS.find((p) => p.value === factor)?.label || "").split(" — ")[0];

  return (
    <div className="rounded-2xl border border-bone/15 bg-bone/5 p-6 sm:p-10" data-testid="battery-stc-calculator">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terra/20">
              <Calculator className="h-5 w-5 text-terra" />
            </span>
            <h3 className="font-display text-2xl font-extrabold text-bone">Battery STC calculator</h3>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-bone/70">
            Home batteries earn Small-scale Technology Certificates upfront under
            the federal Cheaper Home Batteries Program. Estimate the certificate
            value on your next install — then trade them with us.
          </p>

          <div className="mt-8 space-y-7">
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="stc-kwh" className="text-xs font-bold uppercase tracking-[0.14em] text-bone/70">Usable battery capacity</label>
                <span className="font-display text-lg font-extrabold text-terra" data-testid="stc-calc-kwh-label">{kwh.toFixed(1)} kWh</span>
              </div>
              <input
                id="stc-kwh" type="range" min="5" max="100" step="0.5" value={kwh}
                onChange={(e) => setKwh(parseFloat(e.target.value))}
                className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-bone/20 accent-terra"
                data-testid="stc-calc-kwh"
              />
              <div className="mt-1.5 flex justify-between text-[10px] text-bone/50"><span>5 kWh minimum</span><span>100 kWh</span></div>
            </div>

            <div>
              <label htmlFor="stc-period" className="text-xs font-bold uppercase tracking-[0.14em] text-bone/70">Installation period</label>
              <select
                id="stc-period" value={factor} onChange={(e) => setFactor(parseFloat(e.target.value))}
                className="mt-2 h-11 w-full rounded-lg border border-bone/20 bg-forest-900 px-3 text-sm text-bone focus:border-terra focus:outline-none"
                data-testid="stc-calc-period"
              >
                {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <p className="mt-2 text-xs text-bone/50">
                The STC factor steps down over time — earlier periods earn more certificates per kWh.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center rounded-xl border border-bone/15 bg-forest-900/50 p-6 text-center sm:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-bone/60">Your job would create</p>
          <p className="font-display mt-3 text-6xl font-extrabold text-terra sm:text-7xl" data-testid="stc-calc-stcs">
            {stcs}
          </p>
          <p className="mt-1 text-sm text-bone/70">Small-scale Technology Certificates</p>
          <p className="font-display mt-6 text-2xl font-extrabold text-bone" data-testid="stc-calc-value">
            ≈ ${value.toLocaleString("en-AU")}
          </p>
          <p className="mt-1 text-xs text-bone/60">at ${STC_RATE}/STC indicative trade rate</p>
          <p className="mt-6 rounded-lg bg-bone/5 px-4 py-3 text-xs leading-relaxed text-bone/60" data-testid="stc-calc-formula">
            {kwh.toFixed(1)} kWh usable → {counted.toFixed(1)} counted kWh × {factor} ({periodLabel}) = {stcs} STCs
          </p>
        </div>
      </div>
      <p className="mt-6 text-xs leading-relaxed text-bone/50">
        Estimates only. Certificates are calculated on usable capacity in tiers —
        first 14 kWh at 100%, 14–28 kWh at 60%, 28–50 kWh at 15%, and the first
        50 kWh only — with batteries 5–100 kWh nominal capacity eligible. The STC
        market price moves weekly; our trade desk confirms exact figures for
        every job before you commit.
      </p>
    </div>
  );
}

export default function TradeStcPage() {
  return (
    <div data-testid="trade-stc-page">
      {/* hero — battery install image on top */}
      <header className="relative flex min-h-[62vh] items-end overflow-hidden sm:min-h-[68vh]">
        <img src={HERO_IMG} alt="Tesla Powerwall home batteries installed on a garage wall" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest-900/75" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-14 pt-40 sm:pb-20 sm:pt-48">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
            <Overline className="text-terra">For trade partners</Overline>
            <h1 className="font-display mt-3 max-w-3xl text-4xl font-extrabold tracking-tight text-bone sm:text-5xl" data-testid="trade-stc-title">
              Do the jobs. We'll handle the certificates.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-bone/85 sm:text-lg" data-testid="trade-stc-subtitle">
              Install home batteries for your own customers and trade the
              certificates with us. We check every job for compliance, claim the
              battery STCs on your behalf, and pass the value back at trade
              pricing.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact?interest=Trade%20STC%20partnership"
                data-testid="trade-stc-cta-apply"
                className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark"
              >
                Apply to be a trade partner <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="tel:1300768360"
                data-testid="trade-stc-cta-phone"
                className="inline-flex items-center gap-2 rounded-full border border-bone/40 px-7 py-4 text-sm font-bold text-bone transition-colors hover:border-terra hover:text-terra"
              >
                <Phone className="h-4 w-4" /> Call the trade desk — 1300 768 360
              </a>
            </div>
          </motion.div>
        </div>
      </header>

      {/* how it works */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead
            overline="How trading works"
            title="Four steps from install to payment"
            copy="You keep your customer and your margin. We take the compliance load and the certificate paperwork off your plate."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-white p-6 transition-shadow hover:shadow-md" data-testid={`trade-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-bone-dark">
                      <s.icon className="h-5 w-5 text-forest-700" />
                    </span>
                    <span className="font-display text-3xl font-extrabold text-forest-700/15">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display mt-4 text-lg font-bold text-forest-700">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{s.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* battery trend */}
      <section className="border-t border-forest-700/10 bg-bone-dark py-24 md:py-32" data-testid="battery-trend">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead
            overline="The battery boom"
            title="Australia is in the middle of a home battery rush"
            copy="Since the federal Cheaper Home Batteries Program opened, home battery installs have broken record after record — and every quarter has been bigger than the last. Trade partners who can install batteries are winning the work."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TREND_STATS.map((t, i) => (
              <FadeUp key={t.value} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-white p-6" data-testid={`trend-stat-${i}`}>
                  <p className="font-display text-3xl font-extrabold text-forest-700">{t.value}</p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{t.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-xs text-moss/70">
              Source: Clean Energy Regulator quarterly carbon market reports, 2026.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* benefits */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead
            overline="Why homeowners buy"
            title="The benefits that sell themselves"
            copy="Use these talking points on your next job — a battery is the easiest add-on to a solar install to quote."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <FadeUp key={b.title} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-white p-6 transition-shadow hover:shadow-md" data-testid={`battery-benefit-${i}`}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-bone-dark">
                    <b.icon className="h-5 w-5 text-terra" />
                  </span>
                  <h3 className="font-display mt-4 text-lg font-bold text-forest-700">{b.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{b.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* calculator + what we handle */}
      <section className="bg-forest-700 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <BatteryStcCalculator />
          <div className="mt-14 grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <div className="h-full rounded-2xl border border-bone/15 bg-bone/5 p-6 sm:p-10" data-testid="trade-we-handle">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-leaf/20">
                  <ShieldCheck className="h-5 w-5 text-leaf" />
                </span>
                <h3 className="font-display mt-4 text-xl font-bold text-bone">What we handle for you</h3>
                <ul className="mt-5 space-y-3">
                  {WE_HANDLE.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-bone/85">
                      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-leaf" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.08}>
              <div className="flex h-full flex-col justify-between rounded-2xl border border-terra/30 bg-terra/10 p-6 sm:p-10" data-testid="trade-pricing">
                <div>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terra/25">
                    <ClipboardCheck className="h-5 w-5 text-terra" />
                  </span>
                  <h3 className="font-display mt-4 text-xl font-bold text-bone">Trade pricing, every job</h3>
                  <p className="mt-3 text-sm leading-relaxed text-bone/85">
                    Partners get trade rates across our full battery range —
                    Tesla Powerwall 3, GoodWe, Sungrow, Fox ESS and more — plus
                    priority stock access and a dedicated trade contact for
                    pricing, compliance questions and certificate settlements.
                  </p>
                </div>
                <Link
                  to="/contact?interest=Trade%20STC%20partnership"
                  data-testid="trade-pricing-cta"
                  className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark"
                >
                  Get trade pricing <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* final CTA */}
      <section className="bg-bone-dark py-24">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <FadeUp>
            <Overline className="text-terra">Join us</Overline>
            <h2 className="font-display mx-auto mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-forest-700 sm:text-4xl">
              Licensed installer? Let's trade.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-moss sm:text-base">
              Whether you're an electrician, plumber or solar installer — send us
              your details and we'll set up your trade account, check your first
              battery job and get your certificates moving.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact?interest=Trade%20STC%20partnership"
                data-testid="trade-final-cta"
                className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark"
              >
                Become a trade partner <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/installers"
                data-testid="trade-installers-link"
                className="inline-flex items-center gap-2 rounded-full border border-forest-700/25 px-7 py-4 text-sm font-bold text-forest-700 transition-colors hover:border-terra hover:text-terra"
              >
                Meet our installers
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
